import utils from '@ehi/global-marketing-interface/lib/utils';
import { config } from 'utils/config';
import { gmaRedirect } from 'utils/redirect';
import MODAL from 'constants/modals';
import GLOBAL from 'constants/global';
import RESERVATION_LOOKUP from 'constants/reservationLookup';
import WINDOW_OBJECT_KEYS from 'constants/windowObjectKeys';
import { checkinStart } from 'actions/checkin';
import { skipTheCounterStart } from 'actions/skipTheCounter';
import { isReservationCancelledSelector, getReservationRentalState } from 'selectors/reservationSelectors';
import {
  isSkipTheCounterCompletedReservationSelector,
  isCheckedInReservationSelector,
  getCounterBypassEligibleFlag,
  getOnlineCheckInEligibleFlag,
} from 'selectors/reservationFlowSelectors';
import { isCustomPathReservationSelector } from 'selectors/customPathCheckInSelectors';
import { retrieveReservation } from 'actions/reservation/retrieveReservation';
import { openModal } from 'actions/modal/openModal';

const { LOOK_UP_TYPE } = RESERVATION_LOOKUP;

/**
 * @name customPathCheckInLookup
 * @desc Dispatcher that looks up a reservation via a retrieve call;
 *  - If OCI is not eligible and OCI && STC have not been completed,
 *    then dispatch CHECK_IN_INELIGIBLE_MODAL to inform the user.
 *  - If reservation is OCI Eligible and OCI is not activated for the reservation,
 *    then dispatch the action that kickstarts the check-in flow.
 *  - If OCI is activated, STC is eligible and STC has not been completed,
 *    then dispatch the action that kickstarts the skip the counter flow.
 *  - If STC have been activated,
 *    then dispatch STC_ALREADY_ACTIVATED_MODAL to inform the user.
 *  - If STC is not eligible and STC is not activated,
 *    then dispatch STC_INELIGIBLE_MODAL to inform the user.
 *
 * @param {string} origin - the page that originated the check-in start call
 * @returns {Function}
 */
export const customPathCheckInLookup =
  ({ origin }) =>
  (dispatch, getState) => {
    const state = getState();
    const isCheckedIn = isCheckedInReservationSelector(state);
    const isSkipTheCounterCompleted = isSkipTheCounterCompletedReservationSelector(state);
    const isSkipTheCounterEligible = getCounterBypassEligibleFlag(state);
    const isOnlineCheckInEligible = getOnlineCheckInEligibleFlag(state);
    const rentalState = getReservationRentalState(state);
    const isCustomPathReservation = isCustomPathReservationSelector(state);

    // Check Online Check In Eligibility and Completion
    if (!isCheckedIn && !isSkipTheCounterCompleted) {
      !isOnlineCheckInEligible && dispatch(openModal(MODAL.CHECK_IN_INELIGIBLE_MODAL)); // OCI and STC Not Eligible
      isOnlineCheckInEligible && dispatch(checkinStart({ origin })); // Start Onine Check-In Flow
    }

    // Check Skip the Counter Eligibility and Completion
    if (isSkipTheCounterEligible && !isSkipTheCounterCompleted) {
      // A Custom Path Reservation with Vehicle class (Compact) in MCO triggering the "limited inventory notice", which is then preventing STC.
      // Preventing STC flow and redirecting to details page, if a Custom Path res has rentalState as "Pending Confirmation"
      if (rentalState === GLOBAL.PENDING_CONFIRMATION && isCustomPathReservation) {
        window.location.assign(config.getRedirectUrl(WINDOW_OBJECT_KEYS.CUSTOM_PATH_RENTAL_DETAILS));
      } else {
        dispatch(skipTheCounterStart({ origin })); // Start Skip the Counter Flow
      }
    }

    // Once STC is complete both STC eligible and OCI elibile becomes false
    if (!isSkipTheCounterEligible && !isOnlineCheckInEligible) {
      // STC Not Eligible
      if (isSkipTheCounterCompleted) {
        dispatch(openModal(MODAL.STC_ALREADY_ACTIVATED_MODAL)); // OCI and STC completed
      } else {
        dispatch(openModal(MODAL.STC_INELIGIBLE_MODAL));
      }
    }
  };

/**
 * @name checkinLookup
 * @desc Dispatcher that looks up a reservation via a retrieve call;
 * - If OCI is not eligible and OCI && STC have not been completed,
 *   then dispatch CHECK_IN_INELIGIBLE_MODAL to inform the user.
 * - If STC is already activated,
 *   then dispatch STC_ALREADY_ACTIVATED_MODAL to inform the user.
 * - If OCI is not activated for the reservation,
 *   then dispatch the action that kickstarts the check-in flow.
 * - If OCI is already activated,
 *   then dispatch CHECK_IN_ALREADY_ACTIVATED_MODAL to inform the user.
 * - Else start Online Checkin flow.
 * @param {string} origin - the page that originated the check-in start call
 * @returns {Function}
 */
export const checkinLookup =
  ({ origin }) =>
  (dispatch, getState) => {
    const state = getState();
    const isCheckedIn = isCheckedInReservationSelector(state);
    const isOnlineCheckInEligible = getOnlineCheckInEligibleFlag(state);
    const isSkipTheCounterCompleted = isSkipTheCounterCompletedReservationSelector(state);

    !isOnlineCheckInEligible &&
      !isSkipTheCounterCompleted &&
      !isCheckedIn &&
      dispatch(openModal(MODAL.CHECK_IN_INELIGIBLE_MODAL));
    isSkipTheCounterCompleted && dispatch(openModal(MODAL.STC_ALREADY_ACTIVATED_MODAL));

    if (!isSkipTheCounterCompleted) {
      isCheckedIn && dispatch(openModal(MODAL.CHECK_IN_ALREADY_ACTIVATED_MODAL));
      !isCheckedIn && dispatch(checkinStart({ origin }));
    }
  };

/**
 * @name skipTheCounterLookup
 * @desc Dispatcher that looks up a reservation via a retrieve call;
 * - If successful but reservation is not checked in yet,
 *   then dispatch STC_NOT_CHECKED_IN_MODAL to inform the user.
 * - If successful but is STC Ineligible,
 *   then dispatch STC_INELIGIBLE_MODAL to inform the user
 * - If successful but STC is already activated,
 *   then dispatch STC_ALREADY_ACTIVATED_MODAL to inform the user
 * - Else start Skip The Counter flow.
 *
 * @param {string} origin - the page that originated the Skip The Counter start call
 * @returns {Function}
 */
export const skipTheCounterLookup =
  ({ origin }) =>
  (dispatch, getState) => {
    const state = getState();
    const isCheckedIn = isCheckedInReservationSelector(state);
    const isSkipTheCounterCompleted = isSkipTheCounterCompletedReservationSelector(state);
    const isSkipTheCounterEligible = getCounterBypassEligibleFlag(state);

    if (!isCheckedIn && !isSkipTheCounterCompleted) {
      dispatch(openModal(MODAL.STC_NOT_CHECKED_IN_MODAL));
    } else if (!isSkipTheCounterEligible && !isSkipTheCounterCompleted) {
      dispatch(openModal(MODAL.STC_INELIGIBLE_MODAL));
    } else if (isSkipTheCounterCompleted) {
      dispatch(openModal(MODAL.STC_ALREADY_ACTIVATED_MODAL));
    } else {
      dispatch(skipTheCounterStart({ origin }));
    }
  };

/**
 * @name reservationLookup
 * @desc Dispatcher that looks up a reservation via a retrieve call;
 * - If successful, the:
 * - If reservation is cancelled,
 *   then it dispatch CANCELLED_RESERVATION_MODAL to inform the user.
 * - If reservation is Custom Path or the Reservation started in Custom Path Lookup,
 *   then dispatch the action that initalized the custom path flow logic.
 *   All reservation that start in custom path look up must follow customPathCheckInLookup logic.
 * - If reservation is not Custom Path (Retail or Other) and the look up type is CHECKIN_LOOK_UP,
 *   then dispatch the action that initalized the checkinLookup logic.
 * - If reservation is not Custom Path (Retail or Other) and the look up type is STC_LOOK_UP,
 *   then dispatch the action that initalized the skipTheCounterLookup logic.
 * - If reservation is not Custom Path (Retail or Other) and the look up type is CUSTOM_PATH_LOOK_UP or '',
 *   then dispatch customPathCheckInLookup since by default the action check for both OCI and STC reservation status.
 * @param {Object} reservationData - the reservation data used for retrieval
 * @param {string} origin - the page that originated the check-in start call
 * @param {string} origin - the page that originated the check-in start call
 * @returns {Function}
 */
export const reservationLookup =
  ({ reservationData, origin, lookupType = '' }) =>
  (dispatch, getState) =>
    dispatch(retrieveReservation(reservationData, false, lookupType === LOOK_UP_TYPE.CUSTOM_PATH_LOOK_UP)).then(
      (response) => {
        const state = getState();
        const isCutomPathEnabled = config.getIsCustomPathEnabled() || lookupType === LOOK_UP_TYPE.CUSTOM_PATH_LOOK_UP;
        const isCustomPathReservation = isCustomPathReservationSelector(state);

        // Go thourgh OCI & STC logic if is not custom path or custom path is enabled
        if (
          !utils.isArrayNotEmpty(response?.messages) &&
          (!isCustomPathReservation || (isCustomPathReservation && isCutomPathEnabled))
        ) {
          const isReservationCancelled = isReservationCancelledSelector(state);

          // If reservation is cancelled show modal
          isReservationCancelled && dispatch(openModal(MODAL.CANCELLED_RESERVATION_MODAL));

          if (!isReservationCancelled) {
            // All Custom Path Reservations no matter where they come from need to follow customPathCheckInLookup logic
            isCustomPathReservation && dispatch(customPathCheckInLookup({ origin }));

            // If look up started in Custom Path lookup page, then follow customPathCheckInLookup
            if (!isCustomPathReservation) {
              switch (lookupType) {
                case LOOK_UP_TYPE.CHECKIN_LOOK_UP:
                  // If look up started in OCI lookup page then follow checkinLookup logic
                  dispatch(checkinLookup({ origin }));
                  break;
                case LOOK_UP_TYPE.STC_LOOK_UP:
                  // If look up started in STC lookup page, then follow skipTheCounterLookup
                  dispatch(skipTheCounterLookup({ origin }));
                  break;
                default:
                  // If look up started from another page and lookupType is '', then follow customPathCheckInLookup
                  // since the handler has both OCI and STC checks
                  dispatch(customPathCheckInLookup({ origin }));
              }
            }
          }
        } else {
          response?.go_to && gmaRedirect(response.go_to);
        }
      }
    );
